import {
  CoverageStatus,
  CoverageStatusDisplayName,
} from "pmt-types/src/enums/CoverageStatus.ts";
import {
  InsuranceType,
  InsuranceTypeDisplayName,
} from "pmt-types/src/enums/InsuranceType.ts";
import {
  GeneralLiabilityPolicy,
  GeneralLiabilityPolicySchema,
} from "pmt-types/src/models/GeneralLiabilityPolicy.ts";
import { Source } from "pmt-types/src/models/Source.ts";
import FormInput from "../../../components/form/formInput.tsx";
import NumberInput from "../../../components/form/numberInput.tsx";
import PmtForm from "../../../components/form/pmtForm.tsx";
import SearchableSelect from "../../../components/form/searchableSelect.tsx";
import FormRoot from "../../../components/form/style/formRoot.tsx";
import FormSection from "../../../components/form/style/formSection.tsx";
import TabbedCheckbox from "../../../components/form/tabbedCheckbox.tsx";
import TabbedSelect from "../../../components/form/tabbedSelect.tsx";
import TabularInputs from "../../../components/form/tabularInputs.tsx";
import { useMutationSearchInsurers } from "../../../hooks/mutations/useMutationSearchInsurers.ts";
import enumToLabelValue from "../../../utils/transforms/enumToLabelValue.ts";
import { transformSelectInput } from "../../../utils/transforms/transformSelectInput.ts";
import { Maybe } from "../../../utils/types/maybe.ts";

type Props = {
  source: Maybe<Source>;
  onSubmit: (policy: GeneralLiabilityPolicy) => void;
  onCancel: () => void;
};

type LimitTableDefinition = {
  type: string;
  limit: JSX.Element;
};

type OptionTableDefinition = {
  name: string;
  applicable: JSX.Element;
};

export default function GeneralLiabilityPolicyForm({
  source,
  onSubmit,
  onCancel,
}: Props) {
  const { mutateAsync: searchInsurers } = useMutationSearchInsurers();

  const defaultFormValues = {
    typeOfInsurance: InsuranceType.enum.Occurrence,
    limits: {
      eachOccurrence: 2000000,
      damageToRentedPremises: 0,
      medicalExpenses: 0,
      personalAndADVInjury: 2000000,
      generalAggregate: 4000000,
      products: 4000000,
    },
  };

  const existingPolicy = source?.generalLiability;

  const limits = [
    {
      label: "Each Occurrence",
      limitName: "limits.eachOccurrence",
    },
    {
      label: "Damage to Rented Premises (each occurrence)",
      limitName: "limits.damageToRentedPremises",
    },
    {
      label: "Medical Expenses (any one person)",
      limitName: "limits.medicalExpenses",
    },
    {
      label: "Personal and ADV Injury",
      limitName: "limits.personalAndADVInjury",
    },
    {
      label: "General Aggregate",
      limitName: "limits.generalAggregate",
    },
    {
      label: "Products - Comp/OP Aggregate",
      limitName: "limits.products",
    },
  ];

  const options = [
    {
      label: "Gen Agg Limit applies Per Policy",
      optionName: "options.generalAggLimitPerPolicy",
    },
    {
      label: "Gen Agg Limit applies Per Project",
      optionName: "options.generalAggLimitPerProject",
    },
    {
      label: "Gen Agg Limit applies Per Location",
      optionName: "options.generalAggLimitPerLocation",
    },
    {
      label: "Commercial General Liability",
      optionName: "options.commercialGeneralLiability",
    },
  ];

  return (
    <PmtForm<GeneralLiabilityPolicy>
      schema={GeneralLiabilityPolicySchema}
      defaultValues={{
        ...defaultFormValues,
        ...existingPolicy,
      }}
      onSave={onSubmit}
      onCancel={onCancel}
      isSubmitting={false}
      formStyle="inline"
      renderForm={() => (
        <FormRoot>
          <FormSection>
            <FormInput name="policyNumber" label="Policy Number" />
            <FormInput
              name="effectiveDate"
              label="Effective Date"
              type="date"
            />
            <FormInput
              name="expirationDate"
              label="Expiration Date"
              type="date"
            />
            <SearchableSelect
              name="insurer"
              label="Insurer"
              transform={transformSelectInput}
              queryFn={searchInsurers}
            />
            <TabbedSelect
              name="status"
              label="Status"
              tabs={enumToLabelValue(
                CoverageStatus.enum,
                CoverageStatusDisplayName,
              )}
            />
            <TabularInputs
              label="Limits"
              tableDef={[
                {
                  header: "Type",
                  component: (limitDef: LimitTableDefinition) => limitDef.type,
                },
                {
                  header: "Limit",
                  component: (limitDef: LimitTableDefinition) => limitDef.limit,
                },
              ]}
              rows={limits.map((limit) => ({
                type: limit.label,
                limit: (
                  <NumberInput
                    name={limit.limitName}
                    prefix="$"
                    min={0}
                    step={50000}
                  />
                ),
              }))}
            />
            <TabbedSelect
              name="typeOfInsurance"
              label="Type of Insurance"
              tabs={enumToLabelValue(
                InsuranceType.enum,
                InsuranceTypeDisplayName,
              )}
            />
            <TabularInputs
              label="Options"
              tableDef={[
                {
                  header: "Name",
                  component: (optionDef: OptionTableDefinition) =>
                    optionDef.name,
                },
                {
                  header: "Applicable",
                  component: (optionDef: OptionTableDefinition) =>
                    optionDef.applicable,
                },
              ]}
              rows={options.map((option) => ({
                name: option.label,
                applicable: <TabbedCheckbox name={option.optionName} />,
              }))}
            />
          </FormSection>
        </FormRoot>
      )}
    />
  );
}
